/**
 * PLAT-11670 — which transcript elements take part in speaker assignment, and
 * the three operations every speaker writer performs on them: relabel, restore,
 * repair. Stated once here so the writers and the undo-side helpers cannot
 * drift apart again.
 *
 * Background. The transcription provider labels punctuation marks with a
 * speaker exactly as it labels words (on one measured story 1,078 of 1,078
 * marks carried a `speaker_label`); whitespace carries none. From PLAT-9072
 * (section reassignment, 2026-03) until PLAT-11670 every writer relabelled
 * `type === 'text'` elements ONLY, so every reassigned passage kept the
 * PREVIOUS speaker's label on its commas and full stops. Two readers give a
 * mark's label the same weight as a word's, and both misbehaved on that data:
 *   - the Translation Review cross-speaker guard (`useSpanReplace.ts`) saw a
 *     phantom second speaker inside one person's passage and refused with
 *     "Fix one speaker at a time";
 *   - the backend translation segmenter (`arbor-v1/lib/translation/internal.ts`)
 *     closed a fragment before and after every such mark, so the translator
 *     received clause fragments ("Good morning" / "," / "everybody" / ".").
 *
 * The rule, in two separate questions that must not be confused:
 *   - "MAY this element hold a speaker assignment?" A word (`type === 'text'`)
 *     always may, named or not. Any other element may only when it already
 *     carries a `speaker_label`. Whitespace and bare marks are therefore never
 *     given a name: they have no previous label to restore, so writing one would
 *     leave an `override_<id>` that Undo / Unassign All could never clear, and
 *     the label-set readers would keep the person "Assigned".
 *   - "DOES this element have a speaker name right now?" That is
 *     `carriesSpeakerLabel`, and it is not the same question. Whether an element
 *     can be RESTORED is a third question again (`isSpeakerReassigned`), and it
 *     depends only on there being a recorded previous speaker.
 *
 * Known limit. A reassignment that spans two clip segments is split into one
 * command per segment (`VideoEditor.commandReassignSpeakerSection`), and each
 * command sees only its own token array. A mark that is the FIRST token of a
 * segment therefore has no preceding word to be repaired towards and is left
 * alone, even though its owning word is the last token of the previous segment.
 * The repair does not always find the owning word.
 *
 * Known limit. A word that had no speaker name and is given one cannot be
 * undone: there is no previous name to record, so `isSpeakerReassigned` is false
 * for it afterwards. This was equally true before PLAT-11670. Storing "had no
 * name" as a real value and teaching the restore path to clear a name is a
 * data-shape change and its own ticket.
 */

/** The fields a speaker writer reads and writes. Both `TranscriptElement`
 *  (story transcript) and `ClipTranscriptToken` (editor segments) satisfy it. */
export type SpeakerLabelled = {
  type: string;
  speaker_label?: string;
  original_speaker_label?: string;
  paragraph_id?: string;
};

/** True when the element takes part in speaker assignment (see file header). */
export function carriesSpeakerLabel(
  el: SpeakerLabelled | null | undefined,
): el is SpeakerLabelled & { speaker_label: string } {
  return (
    !!el && typeof el.speaker_label === 'string' && el.speaker_label !== ''
  );
}

/**
 * True when the element was section-reassigned and can be restored.
 *
 * Deliberately does NOT require a CURRENT speaker name. `carriesSpeakerLabel`
 * answers "may this element hold a speaker assignment?"; this answers "is there
 * a recorded previous speaker to go back to?". Stored data holds elements with a
 * recorded previous speaker and no current one (or an empty one); requiring a
 * current name stranded them — Undo skipped them and the context menu stopped
 * offering "Undo reassign" (blind review of PLAT-11670, finding 4).
 */
export function isSpeakerReassigned(
  el: SpeakerLabelled | null | undefined,
): boolean {
  return (
    !!el &&
    el.original_speaker_label != null &&
    el.original_speaker_label !== el.speaker_label
  );
}

/** `[start, start + 1, …, end]` (inclusive); empty when `end < start`. */
export function indexRange(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
}

/**
 * True only when both elements name the SAME paragraph. A missing paragraph
 * identifier means "not the same paragraph": plain equality would make
 * `undefined === undefined` true and let every guard pass for data that carries
 * no paragraph identifiers at all — a shape this codebase really produces
 * (`transcriptGrouping.hasMeaningfulParagraphs` falls back to grouping by
 * speaker, and cross-segment text replacement writes words with no paragraph
 * identifier). Without this the widening could carry the NEXT speaker's
 * punctuation into this one (blind review of PLAT-11670, finding 5).
 */
function inSameParagraph(
  a: SpeakerLabelled | null | undefined,
  b: SpeakerLabelled | null | undefined,
): boolean {
  const pa = a?.paragraph_id;
  const pb = b?.paragraph_id;
  return typeof pa === 'string' && pa !== '' && pa === pb;
}

/**
 * The text element that OWNS a mark: the nearest preceding `type === 'text'`
 * element in the same paragraph (`inSameParagraph`, which treats a missing
 * paragraph identifier on either side as "not the same paragraph"). Null when
 * the mark opens its paragraph, when the two do not name the same paragraph, or
 * when no preceding word exists in the array at all — such a mark is left
 * alone. The repair therefore does NOT always find the owning word; see the
 * cross-segment limit in the file header.
 *
 * Verified on real data (PLAT-11670 audit): a closing mark between two
 * paragraphs carries the PREVIOUS paragraph's `paragraph_id` at all 112
 * boundaries measured, so the same-paragraph rule never hands a closing full
 * stop to the next speaker.
 */
function owningTextElement<T extends SpeakerLabelled>(
  elements: readonly T[],
  markIndex: number,
): T | null {
  const mark = elements[markIndex];
  for (let j = markIndex - 1; j >= 0; j--) {
    const el = elements[j];
    if (!el || el.type !== 'text') continue;
    return inSameParagraph(el, mark) ? el : null;
  }
  return null;
}

/**
 * Repair-on-write for marks left stale by the pre-PLAT-11670 text-only writers.
 *
 * For every index in `indices` that is a LABELLED non-text element, compare it
 * with its owning word. When the pair (`speaker_label`, `original_speaker_label`)
 * differs, the mark adopts the word's pair. Its "original" therefore becomes
 * what the WORDS' original is — never the stale value it carried, which was the
 * old speaker's label and would otherwise be recorded as the mark's own
 * pre-reassignment state.
 *
 * Only indices in the written range are examined (never the whole transcript,
 * never on load): a repair changes the stored file and therefore the publish
 * fingerprint, so it must ride on a user action over that passage.
 *
 * Words are never changed by this function.
 */
export function repairStaleMarks<T extends SpeakerLabelled>(
  elements: readonly T[],
  indices: readonly number[],
): T[] {
  const updated = [...elements];
  for (const i of indices) {
    const el = updated[i];
    if (!el || el.type === 'text' || !carriesSpeakerLabel(el)) continue;
    const owner = owningTextElement(updated, i);
    if (!owner || !carriesSpeakerLabel(owner)) continue;
    if (
      owner.speaker_label === el.speaker_label &&
      owner.original_speaker_label === el.original_speaker_label
    ) {
      continue;
    }
    updated[i] = {
      ...el,
      speaker_label: owner.speaker_label,
      original_speaker_label: owner.original_speaker_label,
    };
  }
  return updated;
}

/**
 * Reassign every labelled element at `indices` to `newLabel`, remembering the
 * label it had in `original_speaker_label` (kept if already set, so a second
 * reassignment still restores to the provider's label). Stale marks in the
 * range are repaired first so their recorded original matches their words'.
 * Returns a new array; the input is not mutated.
 */
export function relabelRange<T extends SpeakerLabelled>(
  elements: readonly T[],
  indices: readonly number[],
  newLabel: string,
): T[] {
  const updated = repairStaleMarks(elements, indices);
  for (const i of indices) {
    const el = updated[i];
    if (!el) continue;
    // A WORD always takes part, named or not: the pre-PLAT-11670 writer moved
    // every `type === 'text'` element, and words with no name are really stored
    // (cross-segment text replacement writes them). Leaving one behind splits
    // the paragraph on screen and files it under the OLD speaker. Non-word
    // elements take part only when they already carry a name, which keeps
    // whitespace and bare marks out. (Blind review of PLAT-11670, finding 1.)
    if (el.type !== 'text' && !carriesSpeakerLabel(el)) continue;
    updated[i] = {
      ...el,
      speaker_label: newLabel,
      original_speaker_label: el.original_speaker_label ?? el.speaker_label,
    };
  }
  return updated;
}

/**
 * Undo a section reassignment at `indices`: every reassigned element (word or
 * mark) gets its `original_speaker_label` back and the marker is cleared.
 * Returns a new array; the input is not mutated.
 *
 * Order matters. Restore runs FIRST, from whatever each element itself
 * recorded. Repair runs afterwards and only over the marks that had nothing of
 * their own to go back to. Repairing first copied the preceding word's pair onto
 * a mark and threw away the mark's own recorded previous speaker; when the
 * copied pair was not itself a reassignment the restore loop then skipped the
 * mark, so it kept the word's name and its own recorded value was gone for good
 * (blind review of PLAT-11670, finding 3).
 */
export function restoreRange<T extends SpeakerLabelled>(
  elements: readonly T[],
  indices: readonly number[],
): T[] {
  const updated = [...elements];
  for (const i of indices) {
    const el = updated[i];
    if (!el || !isSpeakerReassigned(el)) continue;
    updated[i] = {
      ...el,
      speaker_label: el.original_speaker_label,
      original_speaker_label: undefined,
    };
  }
  // Marks left stale by the old text-only writers have no recorded previous
  // speaker of their own, so the loop above left them on the OLD speaker's
  // label. Only those adopt their word's (now restored) pair.
  const unrecorded = indices.filter(i => {
    const el = elements[i];
    return !!el && el.type !== 'text' && el.original_speaker_label == null;
  });
  return repairStaleMarks(updated, unrecorded);
}

/**
 * Convert every element at `indices` that currently carries `fromLabel` over to
 * `toLabel` and FORGET its recorded previous speaker. This is the fourth
 * supported speaker write, and it differs from `relabelRange` on purpose: it
 * clears `original_speaker_label` instead of preserving it, because converting a
 * section override into a real voice assignment is not something Undo should be
 * able to walk back. Stale marks in the range are repaired first so they carry
 * `fromLabel` and convert with their words.
 *
 * It lives here rather than as a hand-written loop in the service so that a
 * future change to the shape of a speaker write — stamping another field,
 * clearing a cache key — reaches all four writers instead of three.
 */
export function convertRange<T extends SpeakerLabelled>(
  elements: readonly T[],
  indices: readonly number[],
  fromLabel: string,
  toLabel: string,
): T[] {
  const updated = repairStaleMarks(elements, indices);
  for (const i of indices) {
    const el = updated[i];
    // Not every widened index carries `fromLabel`: an unlabelled mark can join
    // the passage and must stay bare.
    if (!el || el.speaker_label !== fromLabel) continue;
    updated[i] = {
      ...el,
      speaker_label: toLabel,
      original_speaker_label: undefined,
    };
  }
  return updated;
}

/**
 * True when a trailing mark belongs to `word` rather than to somebody else.
 * Three cases qualify:
 *   - the mark carries NO speaker name (nothing of its own to lose);
 *   - its name equals the word's CURRENT name (already moved together);
 *   - its name equals the word's RECORDED PREVIOUS name — the stale case the
 *     widening exists to catch, where the old text-only writer moved the word
 *     and left the mark on the previous speaker.
 * Anything else is a mark somebody deliberately assigned to a different person,
 * for example by drag-selecting a range that began on the comma. Sweeping such a
 * mark into this speaker's set let "Unassign All" / "Reassign All" overwrite it
 * with no undo entry (blind review of PLAT-11670, finding 2). Note this must NOT
 * be tightened to "the names must be equal": a stale mark carries the OLD name
 * by definition, and catching stale marks is the whole point of the widening.
 */
function markBelongsToWord<T extends SpeakerLabelled>(
  mark: T,
  word: T,
): boolean {
  if (!carriesSpeakerLabel(mark)) return true;
  return (
    mark.speaker_label === word.speaker_label ||
    mark.speaker_label === word.original_speaker_label
  );
}

/**
 * Widen a set of WORD indices with the marks that trail each word in the same
 * paragraph (the comma after it, the full stop after that…), up to the next
 * word. Used both by the helpers that find a passage by its label rather than by
 * an explicit range (Unassign All / Reassign All) and by the two range writers,
 * whose range comes from a browser text selection that stops at the last
 * character of the last word and therefore excludes the closing full stop.
 *
 * A mark is added only when it belongs to the word it trails
 * (`markBelongsToWord`) and names the same paragraph (`inSameParagraph`).
 */
export function withTrailingMarks<T extends SpeakerLabelled>(
  elements: readonly T[],
  indices: readonly number[],
): number[] {
  const out = new Set<number>(indices);
  for (const i of indices) {
    const word = elements[i];
    if (!word || word.type !== 'text') continue;
    for (let j = i + 1; j < elements.length; j++) {
      const next = elements[j];
      if (!next || next.type === 'text') break;
      if (inSameParagraph(next, word) && markBelongsToWord(next, word)) {
        out.add(j);
      }
    }
  }
  return [...out].sort((a, b) => a - b);
}
